import { Box, Skeleton, alpha } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { FormComponentBuilder } from 'components/common/form/layout';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { useQuery } from '@tanstack/react-query';
import { elementAPI, FolderData } from 'api/editor/element';
import ElementFolderItem from './folder-selection/ElementFolderItem';
import AddFolderButton from './folder-selection/AddFolderButton';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import useBundleTranslation from 'i18n';
import { NodeModel } from '@minoru/react-dnd-treeview/dist/types';

const EMPTY_FOLDERS: NodeModel[] = [];

export interface FolderSelectionProps extends FormControlProps {
    label: string;
    baseUrl: string;
    readOnly?: boolean;
}

export default function FolderSelection({ controlProps }: FormElementControlPropsType<FolderSelectionProps>) {
    const { t } = useBundleTranslation(['app/components/common/form/form']);

    const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
    const [folderToDelete, setFolderToDelete] = useState<FolderData | null>(null);
    const [elementFolders, setElementFolders] = useState<FolderData[]>([]);

    const readOnly = controlProps.readOnly ?? false;

    const watchedFolderIds = controlProps.form.hookFormWatch(controlProps.name) || [];
    const folderIds: number[] = useMemo(() => watchedFolderIds, [JSON.stringify(watchedFolderIds)]);

    const { data: availableFolders = EMPTY_FOLDERS, isLoading: isLoadingAvailable } = useQuery<NodeModel[], Error>({
        queryKey: ['availableFolders', controlProps.baseUrl],
        queryFn: () => elementAPI.getAvailableFolders(controlProps.baseUrl),
    });

    useEffect(() => {
        if (availableFolders.length > 0 && folderIds.length > 0) {
            const folders: FolderData[] = folderIds
                .map((folderId) => {
                    const folder = availableFolders.find((f) => f.id === folderId);
                    if (!folder) return null;

                    const pathParts: string[] = [];
                    let currentFolder = folder;
                    while (currentFolder.parent && currentFolder.parent !== 0) {
                        const parentFolder = availableFolders.find((f) => f.id === currentFolder.parent);
                        if (!parentFolder) break;
                        pathParts.unshift(parentFolder.text);
                        currentFolder = parentFolder;
                    }

                    return {
                        folderId: Number(folder.id),
                        folderName: folder.text,
                        folderPathParts: pathParts,
                        canEdit: (folder as any).can_edit !== 'N',
                    };
                })
                .filter((f): f is FolderData => f !== null);

            setElementFolders(folders);
        } else if (elementFolders.length > 0) {
            setElementFolders([]);
        }
    }, [folderIds, availableFolders]);

    if (isLoadingAvailable) {
        return <Skeleton height={'38px'} animation={'pulse'} />;
    }

    const onFolderDelete = (folderData: FolderData) => {
        setFolderToDelete(folderData);
        setShowConfirmationPopup(true);
    };

    const popupSettings: PopupSettings = {
        title: t('delete_folder_title'),
        textOK: t('delete_button'),
    };

    const addFolderToForm = (folderId: number) => {
        const newIds = [...folderIds, folderId];
        controlProps.form.hookFormSetValue(controlProps.name, newIds, { shouldDirty: true });
    };

    const removeFolderFromForm = (folderId: number) => {
        const newIds = folderIds.filter((id: number) => id !== folderId);
        controlProps.form.hookFormSetValue(controlProps.name, newIds, { shouldDirty: true });
    };

    const onDeleteConfirm = () => {
        if (folderToDelete) {
            removeFolderFromForm(folderToDelete.folderId);
        }

        setShowConfirmationPopup(false);
    };

    const getFolderPath = () => {
        return (
            folderToDelete && (
                <Box sx={{ color: (theme) => alpha(theme.palette.text.primary, 0.64), wordWrap: 'break-word' }}>
                    {folderToDelete.folderPathParts.length > 0 && (
                        <>
                            {folderToDelete.folderPathParts.join(' / ')}
                            &nbsp;
                        </>
                    )}
                    <Box component={'span'} sx={{ fontWeight: 600 }}>
                        {folderToDelete.folderPathParts.length > 0 && <>{'/'}&nbsp;</>}
                        {folderToDelete.folderName}
                    </Box>
                </Box>
            )
        );
    };

    return (
        <>
            <Box>
                {elementFolders.length > 0 && (
                    <Box sx={{ paddingBottom: '16px' }}>
                        {elementFolders.map((elementFolder, index) => (
                            <ElementFolderItem
                                key={`${elementFolder.folderId} ${index}`}
                                elementFolder={elementFolder}
                                onFolderDelete={onFolderDelete}
                                readOnly={readOnly}
                            />
                        ))}
                    </Box>
                )}
                {!readOnly && (
                    <AddFolderButton
                        selectedFolders={elementFolders}
                        availableFolders={availableFolders}
                        onFolderAdd={addFolderToForm}
                    />
                )}
            </Box>
            {showConfirmationPopup && folderToDelete && (
                <Popup
                    settings={popupSettings}
                    open={showConfirmationPopup}
                    onHide={() => {
                        setShowConfirmationPopup(false);
                        setFolderToDelete(null);
                    }}
                    maxWidth={'popupSm'}
                    onConfirm={onDeleteConfirm}
                >
                    <Box>
                        <Box sx={{ mb: 2 }}>{t('delete_confirm')}</Box>
                        {getFolderPath()}
                    </Box>
                </Popup>
            )}
        </>
    );
}

export class FolderSelectionBuilder extends FormComponentBuilder {
    prepareProps(): FolderSelectionProps {
        return {
            ...this.controlProps,
            label: this.elementProps.componentProps?.label,
            baseUrl: this.elementProps.componentProps?.baseUrl,
            readOnly: this.elementProps.componentProps?.readOnly,
        };
    }
}
